import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { fmtDateTime } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";

const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const buildLink = (n, role) => {
  const type = n.entity_type || n.context_type;
  const id = n.entity_id || n.context_id;
  const cid = n.entity_client_id;
  if (!type || !id) return null;
  if (type === "ticket") return "ticket";
  if (role === "admin") {
    if (type === "client") return `/admin/clients/${id}`;
    if (type === "project") return `/admin/projets/${id}`;
    if (type === "mission") return `/admin/missions/${id}`;
    if (type === "project_conversation") return `/admin/projets/${id}?vue=conversation`;
    if (type === "mission_conversation") return `/admin/missions/${id}?vue=conversation`;
  } else if (role === "collaborator") {
    if (type === "mission") return `/missions/${id}`;
    if (type === "mission_conversation") return `/missions/${id}?vue=conversation`;
    if (type === "project" && cid) return `/clients/${cid}/projets/${id}`;
    if (type === "client") return `/clients/${id}`;
  } else {
    if (type === "project") return `/projets/${id}`;
    if (type === "project_conversation") return `/projets/${id}?vue=conversation`;
    if (type === "identity") return `/identite`;
    if (type === "billing") return `/parametres`;
    if (type === "post") return `/calendrier`;   // « votre calendrier est prêt » mène au calendrier (27/10)
  }
  return null;
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const prevUnread = useRef(0);
  const first = useRef(true);
  const ref = useRef();

  const unread = items.filter((n) => !n.read).length;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const u = data.filter((n) => !n.read).length;
      if (!first.current && u > prevUnread.current) {
        const latest = data.find((n) => !n.read);
        toast(latest ? latest.subject : "Nouvelle notification", {
          icon: <Icons.Bell size={16} className="text-bordeaux" />,
        });
      }
      first.current = false;
      prevUnread.current = u;
      setItems(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => setOpen((o) => !o);

  const isPendingTask = (n) => n.is_task && !n.resolved;
  const sorted = [...items].sort((a, b) => (isPendingTask(b) ? 1 : 0) - (isPendingTask(a) ? 1 : 0));

  const openNotif = async (n, link) => {
    if (!isPendingTask(n) && !n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    const et = n.entity_type || n.context_type;
    if (et === "ticket") {
      setOpen(false);
      window.dispatchEvent(new CustomEvent("open-requests", { detail: { ticketId: n.entity_id || n.context_id } }));
      return;
    }
    if (link) { setOpen(false); nav(link); }
  };

  const resolveNotif = async (e, n) => {
    e.stopPropagation();
    try {
      await api.post(`/notifications/${n.id}/resolve`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, resolved: true, read: true } : x)));
      toast.success("Tâche marquée comme traitée");
    } catch { toast.error("Action impossible"); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell" aria-label="Notifications"
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-white/85 hover:bg-white/10 hover:text-white transition-colors">
        <Icons.Bell size={16} />
        {unread > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-sm border border-marine/15 shadow-lg z-50">
          <div className="px-4 py-3 border-b border-marine/10 text-sm font-display text-marine sticky top-0 bg-white">Notifications</div>
          {items.length === 0 && <div className="px-4 py-6 text-sm text-marine/80 text-center">Aucune notification</div>}
          {sorted.map((n) => {
            const link = buildLink(n, user?.role);
            const pending = isPendingTask(n);
            return (
              <div key={n.id} data-testid="notif-item" onClick={() => openNotif(n, link)}
                className={`px-4 py-3 border-b border-marine/5 last:border-0 ${link ? "cursor-pointer" : ""} ${pending ? "bg-ambre-50/60 border-l-[3px] border-l-ambre-vif hover:bg-ambre-50" : (link ? "hover:bg-sable-clair" : "")} transition-colors`}>
                {pending && <div data-testid="notif-task-badge" className="text-[9px] font-semibold uppercase tracking-wider text-ambre-texte mb-1 flex items-center gap-1"><Icons.AlertCircle size={16} /> À traiter</div>}
                <div className="text-sm font-medium text-marine flex items-center gap-1.5">
                  {!n.read && !pending && <span className="w-1.5 h-1.5 rounded-full bg-rouge shrink-0" />}
                  {n.subject}
                  {link && <Icons.ArrowRight size={16} className="text-bordeaux shrink-0" />}
                </div>
                <div className="text-xs text-marine/80 mt-0.5">{stripHtml(n.body)}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-muet">{fmtDateTime(n.created_at)}</span>
                  <div className="flex items-center gap-2">
                    {link && <span data-testid="notif-open" className="text-[10px] font-semibold uppercase tracking-wider text-bordeaux">Ouvrir</span>}
                    {pending && <button onClick={(e) => resolveNotif(e, n)} data-testid={`notif-resolve-${n.id}`} className="text-[10px] font-semibold uppercase tracking-wider text-bleu hover:underline">Marquer traité</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
